/**
 * programme-day-selector — the day chips: one calendar pill per day this
 * event's catalog actually admits on. Two surfaces mount it: the storefront's
 * event page, directly under <event-header> and above <type-selector>, and
 * the admin console's guest-issue dialog. Their day state lives in two
 * different homes — the storefront's selectedProgrammeDayState singleton and
 * the dialog's own overlay-lifetime @state — and a row reading the storefront
 * singletons cannot serve the second without the admin console writing the
 * buyer's basket state, so the days, the day in force and the in-order set
 * come in as properties and a pick leaves as a `change` event.
 *
 * Pure render + one gesture. Nothing here is stored and nothing is ordered by
 * hand: `days` is TicketType.programmeDaysOf's own output — every day-bound
 * type's own days, in the venue's zone, sorted, so each chip leads to at
 * least the type that minted it. Where no type is day-bound none has an
 * opinion about days and there is no row to draw. A tap reports the day; the
 * list beneath re-derives from it, and what is already picked is never
 * touched, so a multi-day order survives every switch (the chip's own dot is
 * what says which days are already in it).
 *
 * <schmancy-chips> fires `change` on the value the consumer itself bound,
 * first render included, so only a real delta leaves here — an unguarded
 * dispatch would report the controlled value back as if it were intent.
 *
 * `required` on <schmancy-chips> is the whole "never zero-selected" rule: the
 * control ignores a deselect of the chosen chip, so the row cannot reach an
 * empty selection and the list below can never be showing nothing on purpose.
 * Single-select is the default (`multi` stays false) — two days at once would
 * be a filter, and this is a place the buyer stands.
 *
 * NO horizontal padding on the host, deliberately: <schmancy-chips> owns its
 * own horizontal scroller inside its shadow root, so a px-4 out here shrinks
 * that scroll VIEWPORT instead of padding the chips, and the row then
 * terminates at an inset edge reading as a boxed cut rather than content
 * running off-screen. `inline-padding` puts the space on the scroller itself,
 * where the chips scroll THROUGH it and only their resting positions inset —
 * the page's own px-4, the dialog's px-5.
 *
 * The sticky bar belongs to the mounting page, not to this row: it is a
 * page-column contract, resolving against whatever scrollport it lands in,
 * and inside an overlay it would stick flush to the overlay's own top edge
 * while an opaque page ground laid over the overlay's translucent material.
 *
 * The host is `display: contents` so the tag interposes no box of its own —
 * which is what lets the storefront's sticky surface be a direct child of the
 * page's own scroll column, and what makes an event with no programme days
 * cost exactly nothing on either surface: no box, no flex gap, no layout
 * shift, the page it renders today to the pixel.
 *
 * Each pill's accessible name is the whole sentence — "Dienstag, 8.
 * September, 11:00 – 22:00" — carried as visually-hidden CONTENT rather than
 * an aria-label on the chip, because the chip's own name comes from what is
 * slotted into its button and the stacked pill would otherwise announce as
 * three unrelated fragments. The date parts read off the day key in UTC (the
 * key IS the venue's calendar day already, so rendering it in any other zone
 * can shift it by one) and the clock parts off the window's instants in the
 * venue's zone.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import type { TicketType } from "@momo/types";
import { css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { LocalizeMixin } from "../i18n/localize.ts";

@schmancyElement("programme-day-selector")
export class ProgrammeDaySelectorView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	];

	/** Every day this catalog admits on, sorted — `TicketType.programmeDaysOf`'s
	 *  own output, which both surfaces already derive. */
	@property({ attribute: false }) days: readonly TicketType.ProgrammeDay[] = [];

	/** The RESOLVED day, never the raw pick: the caller heals a stored day that
	 *  no longer names a programme day (`TicketType.resolveProgrammeDay`) before
	 *  handing it down, so this row can never be showing nothing on purpose. */
	@property({ attribute: false }) value: string | null = null;

	/** The days the order already holds a line for — the reserved dot, and the
	 *  one thing a multi-day order needs this row to tell it, since switching
	 *  days never touches what is already picked. */
	@property({ attribute: false }) inOrder: ReadonlySet<string> = new Set();

	/** Forwarded to `<schmancy-chips>` so the gutter lands on the scroller
	 *  itself: the page's own `px-4`, the dialog's `px-5`. */
	@property({ attribute: "inline-padding" }) inlinePadding = "px-4";

	override render() {
		if (this.days.length === 0) return nothing;

		return html`
			<schmancy-chips
				class="w-full"
				inline-padding=${this.inlinePadding}
				required
				role="group"
				aria-label=${this.t("storefront.programmeDaySelector.groupAriaLabel")}
				.value=${this.value ?? ""}
				@change=${(e: CustomEvent<{ value: string }>) => {
					// The group's own change is composed, so it escapes this shadow
					// root and reaches the consumer a second time, past the guard
					// below. Stopped here, so the only `change` a consumer ever sees
					// is the one this handler decided to send.
					e.stopPropagation();
					// The group echoes the value we ourselves just bound, first
					// render included — only a real delta is a pick.
					if (e.detail.value === this.value) return;
					this.dispatchEvent(
						new CustomEvent("change", {
							detail: { value: e.detail.value },
							bubbles: true,
							composed: true,
						}),
					);
				}}
			>
				${repeat(
					this.days,
					(d) => d.day,
					(d) => {
						const calendar = new Date(`${d.day}T00:00:00.000Z`);
						const datePart = (opts: Intl.DateTimeFormatOptions) =>
							calendar.toLocaleDateString(this.locale, {
								...opts,
								timeZone: "UTC",
							});
						const clock = (millis: number) =>
							new Date(millis).toLocaleTimeString(this.locale, {
								hour: "2-digit",
								minute: "2-digit",
								timeZone: d.timezone,
							});
						return html`
							<schmancy-filter-chip size="lg" .value=${d.day}>
								<span class="flex w-9 flex-col items-center py-2" aria-hidden="true">
									<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest">
										${datePart({ weekday: "short" })}
									</schmancy-typography>
									<schmancy-typography type="title" token="lg" class="tabular-nums">
										${datePart({ day: "2-digit" })}
									</schmancy-typography>
									<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest">
										${datePart({ month: "short" })}
									</schmancy-typography>
									<!-- Reserved either way, so a day joining the order never
									     changes the row's height. -->
									<span
										class=${this.classMap({
											"mt-1 h-1.5 w-1.5 rounded-full": true,
											"bg-current": this.inOrder.has(d.day),
										})}
									></span>
								</span>
								<schmancy-visually-hidden>
									${this.t(
										this.inOrder.has(d.day)
											? "storefront.programmeDaySelector.dayInOrderAriaLabel"
											: "storefront.programmeDaySelector.dayAriaLabel",
										{
											day: datePart({
												weekday: "long",
												day: "numeric",
												month: "long",
											}),
											opensAt: clock(d.opensAt),
											closesAt: clock(d.closesAt),
										},
									)}
								</schmancy-visually-hidden>
							</schmancy-filter-chip>
						`;
					},
				)}
			</schmancy-chips>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"programme-day-selector": ProgrammeDaySelectorView;
	}
}
